/**
 * Platform-agnostic two-factor helpers: channel predicates and the copy shown
 * on code-entry screens.
 *
 * Storing the trusted-device token is deliberately *not* here. That token is a
 * credential, and where it lives is platform-specific — the web app keeps it in
 * a cookie alongside the session cookies (`apps/web/src/utils/helpers/
 * twoFactorDevice.js`). When native grows a login flow it will want the secure
 * enclave, at which point those three functions belong on `PlatformAuth` in
 * `platform/contract.js` rather than in either app.
 */
import {
  TWO_FACTOR_CHANNEL_EMAIL,
  TWO_FACTOR_CHANNEL_SMS,
  TWO_FACTOR_CHANNEL_TOTP,
} from '../constants/twoFactor';

/**
 * True for authenticator apps, where no code is sent anywhere.
 *
 * @param {string} channel
 * @returns {boolean}
 */
export const isGeneratedTwoFactorChannel = channel =>
  channel === TWO_FACTOR_CHANNEL_TOTP;

/**
 * Heading for a code entry screen.
 *
 * @param {string} channel
 * @returns {string}
 */
export const getTwoFactorHeading = channel => {
  if (isGeneratedTwoFactorChannel(channel)) {
    return 'Enter the code from your app';
  }
  if (channel === TWO_FACTOR_CHANNEL_EMAIL) {
    return 'Check your email';
  }
  return 'Enter the code';
};

/**
 * Tells the user where the code went. `destination` comes back masked from
 * the API, and is null for authenticator apps.
 *
 * @param {{channel: string, destination: ?string}} challenge
 * @returns {string}
 */
export const getTwoFactorIntro = ({channel, destination} = {}) => {
  if (isGeneratedTwoFactorChannel(channel)) {
    return 'Open your authenticator app and enter the 6-digit code it is showing for your Tenant Portal account.';
  }
  if (channel === TWO_FACTOR_CHANNEL_SMS) {
    return destination
      ? `We have sent a 6-digit code by text message to ${destination}.`
      : 'We have sent you a 6-digit code by text message.';
  }
  return destination
    ? `We have sent a 6-digit code to ${destination}.`
    : 'We have sent you a 6-digit code.';
};
